import type { SplitPlan, SplitPlanSegment } from "@CommonTypes/algo/generated-pogger";
import type { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type { useTranslation } from "src/i18n";

type CommonT = ReturnType<typeof useTranslation<"common">>["t"];

/** What the merchant is told about a store's aisle breaks, before it is put into words. */
export type SplitPlanSentence =
  | { kind: "noBrandCrosses"; afterBayNos: number[]; isEveryBreak: boolean }
  | {
      kind: "brandOnBothSides";
      brand: string;
      afterBayNo: number;
      /** Null when both sides are the same block, so there is nothing to tell them apart by. */
      sides: { left: string; right: string } | null;
    }
  | { kind: "couldNotHold"; bayNos: number[]; products: string[] }
  | { kind: "hasNo"; bayNos: number[]; sections: string[] }
  | { kind: "moved"; what: string; after: string | null; toFrontOf: string | null };

const brandIndexOf: Record<SplitPlan["brand_level"], number> = { cdt0: 0, cdt1: 1, cdt2: 2 };

/**
 * OWA sends cdt1 to the pogger as `variant/cdt1`, so a path component can carry its parent's
 * name as a prefix; `cdtNames` wins where it has one.
 */
const displayName = (cdtNames: ReadonlyMap<string, string>, raw: string, parent?: string | null) =>
  cdtNames.get(raw) ??
  (parent && raw.startsWith(`${parent}/`) ? raw.slice(parent.length + 1) : raw);

const displayPath = (cdtNames: ReadonlyMap<string, string>, path: readonly string[]) =>
  path.map((component, index) => displayName(cdtNames, component, path[index - 1]));

const lastBayOf = (segment: SplitPlanSegment | undefined) =>
  segment && segment.bay_nos.length > 0 ? Math.max(...segment.bay_nos) : null;

const MOVED_AFTER = /^after (.+)$/;

/**
 * `cdtNames` maps a raw CDT component or product code to the name a merchant knows; anything it
 * lacks is shown as the plan carries it.
 */
export const describeSplitPlan = (
  plan: SplitPlan | undefined,
  cdtNames: ReadonlyMap<string, string>,
): SplitPlanSentence[] => {
  if (!plan) return [];

  const segments = [...plan.segments].sort((a, b) => a.index - b.index);
  const segmentByIndex = new Map(segments.map((segment) => [segment.index, segment]));
  const breaks = segments
    .slice(0, -1)
    .map(lastBayOf)
    .filter((afterBayNo): afterBayNo is number => afterBayNo !== null);
  const brandIndex = brandIndexOf[plan.brand_level];

  const crossings: Extract<SplitPlanSentence, { kind: "brandOnBothSides" }>[] = [];
  const crossedBreaks = new Set<number>();
  for (const cut of plan.cuts ?? []) {
    const afterBayNo = lastBayOf(segmentByIndex.get(cut.left_segment));
    if (afterBayNo === null) continue;

    const left = displayPath(cdtNames, cut.left_block);
    const right = displayPath(cdtNames, cut.right_block);
    const brand = left[Math.min(brandIndex, left.length - 1)];
    if (brand !== right[Math.min(brandIndex, right.length - 1)]) continue;

    const differs = (index: number) => index !== brandIndex && left[index] !== right[index];
    const leftParts = left.filter((_, index) => differs(index));
    const rightParts = right.filter((_, index) => differs(index));
    crossedBreaks.add(afterBayNo);
    crossings.push({
      kind: "brandOnBothSides",
      brand,
      afterBayNo,
      sides:
        leftParts.length === 0 && rightParts.length === 0
          ? null
          : { left: [brand, ...leftParts].join(" "), right: [brand, ...rightParts].join(" ") },
    });
  }

  const cleanBreaks = breaks.filter((afterBayNo) => !crossedBreaks.has(afterBayNo));
  const sentences: SplitPlanSentence[] = [
    ...crossings.sort((a, b) => a.afterBayNo - b.afterBayNo),
    ...(cleanBreaks.length > 0
      ? [
          {
            kind: "noBrandCrosses" as const,
            afterBayNos: cleanBreaks,
            isEveryBreak: crossedBreaks.size === 0,
          },
        ]
      : []),
  ];

  for (const trim of [...(plan.trimmed ?? [])].sort((a, b) => a.segment_index - b.segment_index)) {
    const segment = segmentByIndex.get(trim.segment_index);
    if (!segment || trim.item_ids.length === 0) continue;
    sentences.push({
      kind: "couldNotHold",
      bayNos: segment.bay_nos,
      products: trim.item_ids.map((itemId) => cdtNames.get(itemId) ?? itemId),
    });
  }

  for (const segment of segments) {
    const missing = segment.missing_must_have ?? [];
    if (missing.length === 0) continue;
    sentences.push({
      kind: "hasNo",
      bayNos: segment.bay_nos,
      sections: missing.map((section) => displayName(cdtNames, section)),
    });
  }

  for (const move of plan.moves ?? []) {
    const after = MOVED_AFTER.exec(move.where)?.[1] ?? null;
    sentences.push({
      kind: "moved",
      what: displayName(cdtNames, move.what, move.within),
      after: after === null ? null : displayName(cdtNames, after, move.within),
      toFrontOf: after === null && move.within ? displayName(cdtNames, move.within) : null,
    });
  }

  return sentences;
};

/** Product names by code, from every item the pog holds, ranged or not. */
export const splitPlanNamesOf = (pog: PogNonCircular | null | undefined) => {
  const names = new Map<string, string>();
  if (!pog) return names;

  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.items) names.set(item.productCode, item.name);
    }
  }
  for (const item of pog.unrangedItems) names.set(item.productCode, item.name);
  return names;
};

const MAX_LISTED_PRODUCTS = 5;

const joinWords = (words: readonly string[], conjunction: string) =>
  words.length <= 1
    ? words.join("")
    : `${words.slice(0, -1).join(", ")} ${conjunction} ${words[words.length - 1]}`;

const bayRange = (bayNos: readonly number[]) => {
  const sorted = [...bayNos].sort((a, b) => a - b);
  return { first: sorted[0], last: sorted[sorted.length - 1], isOneBay: sorted.length === 1 };
};

export const wordSplitPlanSentence = (t: CommonT, sentence: SplitPlanSentence): string => {
  switch (sentence.kind) {
    case "noBrandCrosses": {
      const { afterBayNos, isEveryBreak } = sentence;
      if (afterBayNos.length === 1) {
        return t("aisleBreaks.result.noBrandCrossesOne", { bayNo: afterBayNos[0] });
      }
      const bayNos = joinWords(afterBayNos.map(String), t("aisleBreaks.and"));
      if (!isEveryBreak) return t("aisleBreaks.result.noBrandCrossesThese", { bayNos });
      return afterBayNos.length === 2
        ? t("aisleBreaks.result.noBrandCrossesEither", { bayNos })
        : t("aisleBreaks.result.noBrandCrossesAny", { bayNos });
    }
    case "brandOnBothSides": {
      const { brand, afterBayNo, sides } = sentence;
      return sides
        ? t("aisleBreaks.result.brandOnBothSides", { brand, bayNo: afterBayNo, ...sides })
        : t("aisleBreaks.result.brandOnBothSidesPlain", { brand, bayNo: afterBayNo });
    }
    case "couldNotHold": {
      const { first, last, isOneBay } = bayRange(sentence.bayNos);
      const count = sentence.products.length;
      const listed = sentence.products.slice(0, MAX_LISTED_PRODUCTS).join(", ");
      const products =
        count > MAX_LISTED_PRODUCTS
          ? t("aisleBreaks.listMore", { list: listed, count: count - MAX_LISTED_PRODUCTS })
          : listed;
      if (isOneBay) {
        return count === 1
          ? t("aisleBreaks.result.bayCouldNotHoldOne", { bayNo: first, products })
          : t("aisleBreaks.result.bayCouldNotHoldMany", { bayNo: first, count, products });
      }
      return count === 1
        ? t("aisleBreaks.result.baysCouldNotHoldOne", { first, last, products })
        : t("aisleBreaks.result.baysCouldNotHoldMany", { first, last, count, products });
    }
    case "hasNo": {
      const { first, last, isOneBay } = bayRange(sentence.bayNos);
      const sections = joinWords(sentence.sections, t("aisleBreaks.or"));
      return isOneBay
        ? t("aisleBreaks.result.bayHasNo", { bayNo: first, sections })
        : t("aisleBreaks.result.baysHaveNo", { first, last, sections });
    }
    case "moved": {
      const { what, after, toFrontOf } = sentence;
      if (after !== null) return t("aisleBreaks.result.movedAfter", { what, after });
      return toFrontOf !== null
        ? t("aisleBreaks.result.movedToFrontOf", { what, within: toFrontOf })
        : t("aisleBreaks.result.movedToFront", { what });
    }
  }
};
